import { applicationConfiguration } from 'app/modules/shared'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Accordion, Nav, Tab } from 'react-bootstrap'
import { Trans } from 'react-i18next'
import ScannerModule from '../../modules/reports/features/scan/scanner-module'
import ThreatHunting from '../../modules/reports/features/search/home-threat-hunting'
import { allowedFeaturesStorage } from 'app/modules/auth/helper/storage'
import { Feature } from 'app/modules/auth/types'
import { Banner } from '../banner'
import { MaintenanceNote } from '../notes'
import { News } from './news'
import { NewsItemType } from 'app/modules/admin/features/settings/appearance/news/types'
import { settingsService } from 'app/modules/admin/services'
import { LicenseQuotaNotification } from '../notes/license-quota-note'
import { HomeTheme } from './types'
import { HomeTitle } from './home-title'

const HomePage = (): JSX.Element => {
  const { _t } = useTypedTranslation()
  const config = useContext(applicationConfiguration)
  const [activeTab, setActiveTab] = useState('analyze')
  const [news, setNews] = useState<NewsItemType[]>([])
  const allowedFeatures = allowedFeaturesStorage.getFeatures() || []
  const showBanner = allowedFeatures.indexOf(Feature.Banner) !== -1
  const isCommunity = config.buildType === 'community'
  const appDescKey = !isCommunity ? 'upload-file-description-commercial' : 'upload-file-description'
  const isScanBlocked = config.maintenance.blockScan
  const homeTheme = config.homeTheme.replace(/_/g, '-')
  const isFancyTheme = homeTheme !== HomeTheme.Simple
  const activeNews = useMemo(() => {
    return news.filter((item) => item.active).sort((a, b) => a.order - b.order)
  }, [news])

  const getActiveTabClass = (eventKey: string, className: string) => (eventKey === activeTab ? className : '')

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const fetchedNews: NewsItemType[] = await settingsService.getNews()
        setNews(fetchedNews)
      } catch (error) {
        console.error(error)
      }
    }

    fetchNews()
  }, [])

  return (
    <div className={`home-container ${homeTheme}`}>
      <div className="home-container-background" />

      <div className="home-container-header home-container-item">
        {showBanner ? <Banner /> : null}
        {isScanBlocked && showBanner ? <div>&nbsp;</div> : null}
        {config.licenseQuotaNotification ? <LicenseQuotaNotification data={config.licenseQuotaNotification} /> : null}
        {isScanBlocked ? <MaintenanceNote message={config.maintenance.blockScanNotification} /> : null}
        <HomeTitle isCommunity={isCommunity} isFancyTheme={isFancyTheme} />
      </div>

      {/* ================== Desktop view ================== */}
      <div className={`tabbed-container hide-mobile home-container-item ${isFancyTheme ? 'with-shadow' : ''}`}>
        <Tab.Container id="left-tabs" activeKey={activeTab}>
          <Nav className="tabbed-container-tabs">
            <Nav.Item
              className={`tabbed-container-tab ${getActiveTabClass('analyze', 'tabbed-container-tab-selected')}`}
            >
              <Nav.Link
                className="text-main text-m text-upper"
                eventKey="analyze"
                onClick={() => setActiveTab('analyze')}
              >
                {_t('analyze-file')}
              </Nav.Link>
            </Nav.Item>
            {/* <Nav.Item className={getTabClass('reputation-lookup')}>
              <Nav.Link
                className="text-main text-m"
                eventKey="reputation-lookup"
                onClick={() => setActiveTab('reputation-lookup')}
              >
                <i className="ds3 ds3-search me-1" />
                Reputation Lookup - To be added later
              </Nav.Link>
            </Nav.Item> */}
            <Nav.Item
              className={`tabbed-container-tab ${getActiveTabClass('threat-hunting', 'tabbed-container-tab-selected')}`}
            >
              <Nav.Link
                className="text-main text-m text-upper"
                eventKey="threat-hunting"
                onClick={() => setActiveTab('threat-hunting')}
              >
                {_t('threat-hunting')}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className="d-flex flex-column h-100 tabbed-container-main position-relative">
            <div className="owl-background" />
            <div className="shadow-background" />

            <Tab.Content className="tabbed-container-content">
              <Tab.Pane className="tabbed-container-pane" eventKey="analyze">
                <ScannerModule />
              </Tab.Pane>
              {/* <Tab.Pane className="tabbed-container-pane" eventKey="reputation-lookup">Reputation Lookup tab - to be added later</Tab.Pane> */}
              <Tab.Pane className="tabbed-container-pane" eventKey="threat-hunting">
                <ThreatHunting />
              </Tab.Pane>
            </Tab.Content>
          </div>
        </Tab.Container>
      </div>

      {/* ================== Mobile view ================== */}
      <div className="mobile-tabbed-container hide-desktop home-container-item accordions-container full-width">
        <div className="owl-background" />

        <Accordion defaultActiveKey="analyze" flush>
          <Accordion.Item eventKey="analyze">
            <Accordion.Header>
              <Nav.Link className="text-main text-m gap-x-2 d-flex">
                <i className="ds3-icon ds3-upload" />
                {_t('analyze-file')}
              </Nav.Link>
            </Accordion.Header>

            <Accordion.Body className="accordion-no-overflow">
              <ScannerModule />
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="threat-hunting">
            <Accordion.Header>
              <Nav.Link className="text-main text-m gap-x-2 d-flex">
                <i className="ds3-icon ds3-crosshair" />
                {_t('threat-hunting')}
              </Nav.Link>
            </Accordion.Header>

            <Accordion.Body className="accordion-no-overflow">
              <ThreatHunting />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>

      <div className="home-container-item position-relative">
        <div className="text-main text-muted home-description">
          <Trans
            i18nKey={appDescKey}
            components={{
              appTitle: config.appTitle as unknown as JSX.Element,
              link1: <a href="https://www.filescan.io" target="_blank" rel="noreferrer" />,
              link2: isCommunity ? (
                <a
                  href="https://info.opswat.com/leverage-adaptive-threat-analysis-to-detect-highly-evasive-malware?utm_source=fsio&utm_medium=mpatattxtl&utm_campaign=24_v1"
                  target="_blank"
                  rel="noreferrer"
                />
              ) : (
                <></>
              ),
            }}
          />
        </div>

        {isCommunity ? (
          <a
            href="https://www.opswat.com/products/metadefender/sandbox?utm_source=fsio&utm_medium=mplmtxtl&utm_campaign=24_v1"
            target="_blank"
            className="d-block mt-2 text-justify text-main text-muted underlined text-nowrap"
            rel="noreferrer"
          >
            {_t('learn-more')}
          </a>
        ) : null}

        {activeNews.length ? <News items={activeNews} /> : null}
      </div>
    </div>
  )
}

export default HomePage
